import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { fyLabel } from '../utils/fy';
import HelpCenter from './HelpCenter';

const ROLE_LABELS = {
  owner_admin: 'Owner/Admin',
  customer_owner: 'Admin',
  consultant_staff: 'Consultant/Staff',
  client: 'Client',
};

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

function DashboardHeader({ onMenuClick }) {
  const {
    companies,
    selectedCompany,
    selectedCompanyId,
    selectCompany,
    fyStartYear,
    setFyStartYear,
    openAddModal,
  } = useCompany();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const changeFY = (delta) => {
    const newYear = fyStartYear + delta;
    setFyStartYear(newYear);
    if (location.pathname === '/calendar') {
      navigate(`/calendar?month=4&year=${newYear}`);
    }
  };

  return (
    <header className="dash-header">
      <div className="dash-header-left">
        {/* Only rendered on screens where the sidebar has collapsed into a
            drawer; CSS hides it at desktop widths. */}
        <button
          type="button"
          className="dash-nav-toggle"
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div>
          <div className="dash-header-brand">
            KROSI<span>FIN</span>
          </div>
          <div className="dash-header-tagline">by NeedCFO</div>
        </div>
      </div>

      <div className="dash-header-right">
        <div className="header-company-dropdown-wrap">
          <button
            type="button"
            className="dash-company-select"
            onClick={() => setDropdownOpen((o) => !o)}
          >
            {selectedCompany ? selectedCompany.name : 'Select company'} ▾
          </button>

          {dropdownOpen && (
            <div className="header-company-dropdown">
              {companies.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={c.id === selectedCompanyId ? 'active' : ''}
                  onClick={() => {
                    selectCompany(c.id);
                    setDropdownOpen(false);
                  }}
                >
                  {c.name}
                </button>
              ))}
              <button
                type="button"
                className="add-company-option"
                onClick={() => {
                  setDropdownOpen(false);
                  openAddModal();
                }}
              >
                + Add Company
              </button>
            </div>
          )}
        </div>

        <Link
          to="/dashboard/consolidated"
          className={`dash-consolidated-btn ${location.pathname === '/dashboard/consolidated' ? 'active' : ''}`}
        >
          Consolidated View
        </Link>

        <div className="dash-fy-selector">
          <button
            className="dash-fy-arrow"
            type="button"
            onClick={() => changeFY(-1)}
            aria-label="Previous financial year"
          >
            &larr;
          </button>
          <span className="dash-fy-label">{fyLabel(fyStartYear)}</span>
          <button
            className="dash-fy-arrow"
            type="button"
            onClick={() => changeFY(1)}
            aria-label="Next financial year"
          >
            &rarr;
          </button>
        </div>

        <button
          type="button"
          className="dash-consolidated-btn"
          onClick={() => setHelpOpen(true)}
          title="Guide and FAQs"
        >
          ? Help
        </button>

        <div className="header-company-dropdown-wrap">
          <button
            type="button"
            className="dash-avatar"
            onClick={() => setUserMenuOpen((o) => !o)}
            title={user?.name}
          >
            {initials(user?.name)}
          </button>

          {userMenuOpen && (
            <div className="header-company-dropdown align-right">
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{user?.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{ROLE_LABELS[user?.role] || user?.role}</div>
              </div>
              <button type="button" onClick={() => { setUserMenuOpen(false); logout(); }}>
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>

      <HelpCenter open={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
}

export default DashboardHeader;
